import React from "react";
import { motion } from "framer-motion";

interface CinematicOverlayProps {
  title: string;
  subtitle: string;
  onComplete?: () => void;
}

const LEAGUE_LOGO_URL =
  "https://dpsrbqvcukoomuupjfdm.supabase.co/storage/v1/object/public/draft%20bucket/Empire%20League%20Logo.png";

export const CinematicOverlay: React.FC<CinematicOverlayProps> = ({
  title,
  subtitle,
  onComplete,
}) => {
  return (
    <motion.div
      className="cinematic-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8 }}
      onAnimationComplete={() => {
        if (onComplete) {
          // This creates the 6-second "hold" before transitioning back
          // to the PublicTracker UI
          setTimeout(onComplete, 6000);
        }
      }}
    >
      {/* Background Layer */}
      <div className="sky-gradient">
        <div className="central-glow" />
      </div>

      {/* Decorative Palm Trees */}
      <div className="palm-container">
        <img
          src="https://www.transparentpng.com/download/palm-tree/X6XG7m-palm-tree-silhouette-clipart-transparent.png"
          className="palm palm-left"
          alt=""
        />
        <img
          src="https://www.transparentpng.com/download/palm-tree/X6XG7m-palm-tree-silhouette-clipart-transparent.png"
          className="palm palm-right"
          alt=""
        />
      </div>

      {/* Content Layer */}
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, scale: 1.05, filter: "blur(20px)" }}
        transition={{ duration: 1, ease: "easeOut" }}
        className="content-box"
      >
        <motion.img
          animate={{
            filter: [
              "drop-shadow(0 0 20px #22565a)",
              "drop-shadow(0 0 50px #22565a)",
              "drop-shadow(0 0 20px #22565a)",
            ],
          }}
          transition={{ duration: 3, repeat: Infinity }}
          src={LEAGUE_LOGO_URL}
          className="logo-cinematic-img"
        />

        <h1 className="cinematic-title">{title}</h1>
        <h2 className="cinematic-subtitle">{subtitle}</h2>
      </motion.div>

      <style>{`
        .cinematic-container { 
          position: fixed; 
          inset: 0; 
          background: #000; 
          display: flex; 
          align-items: center; 
          justify-content: center; 
          overflow: hidden; 
          z-index: 99999; 
          font-family: 'Oswald', sans-serif; 
        }
        .sky-gradient { 
          position: absolute; 
          inset: 0; 
          background: radial-gradient(circle at 50% 50%, #112e31 0%, #000 100%); 
        }
        .central-glow { 
          position: absolute; 
          width: 100vw; 
          height: 100vh; 
          background: radial-gradient(circle, rgba(34, 86, 90, 0.15) 0%, transparent 70%); 
          filter: blur(100px); 
        }
        .palm-container { 
          position: absolute; 
          inset: 0; 
          pointer-events: none; 
        }
        .palm { 
          position: absolute; 
          bottom: -80px; 
          height: 110%; 
          filter: brightness(0); 
          opacity: 0.15; 
        }
        .palm-left { left: -180px; transform: scaleX(-1); }
        .palm-right { right: -180px; }
        .content-box { 
          position: relative; 
          z-index: 100; 
          text-align: center; 
          display: flex; 
          flex-direction: column; 
          align-items: center; 
        }
        .logo-cinematic-img { height: 180px; margin-bottom: 30px; object-fit: contain; }
        .cinematic-title { 
          color: #f9eeda; 
          font-size: 5.5rem; 
          font-weight: 900; 
          margin: 0; 
          letter-spacing: -2px; 
          line-height: 0.9; 
          text-transform: uppercase; 
          text-shadow: 0 0 30px rgba(34, 86, 90, 0.5);
        }
        .cinematic-subtitle { 
          color: #22565a; 
          font-size: 1.5rem; 
          letter-spacing: 12px; 
          text-transform: uppercase; 
          margin-top: 20px; 
        }
      `}</style>
    </motion.div>
  );
};
